import {IReg, REG} from "lib/commons/registry";
import {IElementWedlet, IWedlet} from "back/edit/wed/wedlets/wedlet";
import {BaseElementAsync, MsgLabel, OMsgLabelInit, OSkinableInit} from "back/commons/basis";
import {DOMSH} from "lib/commons/xml/domsh";
import {ActionBtn} from "back/commons/widgets/buttons";
import {JSX} from "lib/commons/xml/dom";
import {IItemUiEnv} from "back/wsp/views/itemMain";
import {IJmlNode, IJmlSubSet} from "lib/commons/xml/jml";
import {SkinClass, STYLER} from "lib/styler/styler";
import {OpenPropsWspAction} from "back/wsp/actions/wspActions";
import {Wsp} from "lib/wsp/wsp";
import {StylerSkinCLass} from "back/styler/widgets/widgets";
import {SkinClassStlPreview} from "back/styler/widgets/previews";
import {POPUP, PopupTooltip} from "back/commons/widgets/popups";

/**
 * Skinset : bandeau des propriétés
 */
export class StylerSkinsetPropsUI extends BaseElementAsync implements IElementWedlet {
	wedlet: IWedlet;

	reg: IReg<IItemUiEnv>;

	contentElt: HTMLDivElement;

	private _onWspLiveStateChange: any;

	static OPEN_WSP_PROPS_ACT = new OpenPropsWspAction().setLabel("Activer...").setDescription("Activer une extension de stylage...");

	async _initialize(init: OSkinableInit): Promise<void> {
		this.reg = this.findReg(init);
		const sr = this.attachShadow(DOMSH.SHADOWDOM_INIT);
		this._initAndInstallSkin(this.localName, init);
		// Inutile pour le moment vues les infos affichées
		// this.reg.env.longDescChange.add(()=>this.redraw());
		this.contentElt = sr.appendChild(<div class="props"/>) as HTMLDivElement;
		this._onWspLiveStateChange = this.onWspLiveStateChange.bind(this);
		this.reg.env.place.eventsMgr.on("wspLiveStateChange", this._onWspLiveStateChange);
		await this.refresh();
	}

	protected async onWspLiveStateChange(wsp: Wsp) {
		if (wsp !== this.reg.env.wsp) return;
		await this.refresh();
	}

	disconnectedCallback() {
		this.reg.env.place.eventsMgr.removeListener("wspLiveStateChange", this._onWspLiveStateChange);
	}

	configWedletElt(tpl: Element, wedlet: IWedlet) {
		this.wedlet = wedlet;
	}

	refreshBindValue(val: IJmlNode | string | null, children?: IJmlSubSet): void {
		this.refresh();
	}

	protected async _refresh() {
		if (this.contentElt) {
			this.contentElt.innerHTML = "";
			const skinClassesStyler = STYLER.getSkinClassesStl(this.reg.env.wsp);
			if (!this.reg.env.wsp.wspType) return;

			// - Liste des extensions de stylages
			let wpOptionSkinTitles = new Set();
			if (this.reg.env.wsp.wspType.wspOptions)
				this.reg.env.wsp.wspType.wspOptions.forEach((entry) => wpOptionSkinTitles.add(entry.title));
			let wpOptionSkinTitlesStr = [...wpOptionSkinTitles].sort().join(", ");

			this.contentElt.appendChild(<div class="entry">
				<span class="key">Extensions de stylage actives :</span>
				<span class="value">
					<MsgLabel class="msg" î={{label: wpOptionSkinTitlesStr || "Aucune. Veuillez installer/activer une extension de stylage.", level: wpOptionSkinTitlesStr ? null : 'warning'} as OMsgLabelInit}/>
				</span>
				<span class="acts">
					{ActionBtn.buildButton(StylerSkinsetPropsUI.OPEN_WSP_PROPS_ACT, this.reg, 'custom', this.contentElt)}
				</span>
			</div>);

			// - Liste des skins activables
			if (skinClassesStyler && Object.values(skinClassesStyler).length) {
				let skinClassesStylerValueElt = <span class="value"/>;
				let listSkinsClasses: Set<SkinClass> = new Set();
				Object.values(skinClassesStyler).forEach((entry) => listSkinsClasses.add(entry.skinClass(this.reg.env.wsp)));

				Array.of(...listSkinsClasses)
					.sort((e1, e2) => e1.name.localeCompare(e2.name))
					.forEach((entry) => {
						skinClassesStylerValueElt.appendChild(<StylerSkinCLass î={{skinCLass: entry.class, reg: this.reg}}/>);
					});
				this.contentElt.appendChild(<div class="entry">
					<span class="key">Familles de générateurs stylables :</span>
					{skinClassesStylerValueElt}
					<span class="acts"/>
				</div>);
			}
		}
	}
}

REG.reg.registerSkin('styler-skinset-propsui', 1, /* language=CSS */ `
	:host {
		display: block;
		color: var(--info-color);
		background-color: var(--panel-bgcolor);
		margin: 0em 1em;
		padding: .5em;
		font-size: .8rem;
		border-radius: 5px;
	}

	.props {
		display: grid;
		grid-template-columns: auto 1fr auto;
		grid-template-rows: auto;
		grid-column-gap: 3em;
	  margin-inline-start: .5em;
  }

  .entry {
	  display: contents;
  }

  .key {
	  color: var(--alt2-color);
  }

  .msg {
	  justify-content: unset;
  }

  styler-skinclass + styler-skinclass {
	  margin-inline-start: 1em;
  }
`);

customElements.define('styler-skinset-propsui', StylerSkinsetPropsUI);

/**
 * SkinClassStl : bandeau des propriétés du skinClassStl
 */
export class StylerSkinClassPropsUI extends BaseElementAsync implements IElementWedlet {
	wedlet: IWedlet;

	reg: IReg<IItemUiEnv>;

	tooltip: PopupTooltip;

	private _onWspLiveStateChange: any;

	async _initialize(init: OSkinableInit): Promise<void> {
		this.reg = this.findReg(init);
		const sr = this.attachShadow(DOMSH.SHADOWDOM_INIT);
		this._initAndInstallSkin(this.localName, init);

		const skinClassStlCode = (this.reg.env.longDesc as any).itAttr_skClassStl;
		const skinClassStl = STYLER.getSkinClassStl(this.reg.env.wsp, skinClassStlCode);
		const skinClass = skinClassStl.skinClass(this.reg.env.wsp);

		const infosBtn = sr.appendChild(<img class='infoBtn'/>);
		sr.appendChild(<div class="spacer"/>);
		this.tooltip = POPUP.attachTooltip(infosBtn, <SkinClassStlPreview î={{shortDesc: this.reg.env.longDesc, reg: this.reg, mode: 'details'}}/>);

		sr.appendChild(<StylerSkinCLass î={{skinCLass: skinClass.class, reg: this.reg}}/>);
	}

	configWedletElt(tpl: Element, wedlet: IWedlet) {
		this.wedlet = wedlet;
	}

	refreshBindValue(val: IJmlNode | string | null, children?: IJmlSubSet): void {
	}

}

REG.reg.registerSkin('styler-skinclassstyler-propsui', 1, /* language=CSS */ `
	:host {
		align-items: center;
		display: flex;
		flex: 1;
	}

	.infoBtn {
		width: var(--icon-size);
		height: var(--icon-size);
		content: url(/@skin@/styler/widgets/info.svg);
		cursor: pointer;
	}

	.spacer {
		flex: 1;
	}
`);

customElements.define('styler-skinclassstyler-propsui', StylerSkinClassPropsUI);
